<template>
  <modal-wrap :name="modalName" class="invoice-address-modal" :hide-footer="true">
    <template #modal-title>{{ addressData?.id ? 'Edit invoice address' : 'Add invoice address' }}</template>
    <template #default>
      <form class="invoice-address-form" novalidate @submit.prevent="onSubmit">
        <p class="address-context">{{ company?.name }} · Client № {{ company?.clientNumber }}</p>
        <p class="address-description">Used for billing. Sender addresses are managed separately.</p>
        <fieldset :disabled="saving">
          <legend class="visually-hidden">Invoice address</legend>
          <div class="address-field">
            <label for="invoice-country">Country</label>
            <select
              id="invoice-country"
              v-model="customer.countryCode"
              class="form-control"
              autocomplete="billing country"
              :aria-invalid="!!errors.countryCode"
              aria-describedby="invoice-country-error"
            >
              <option value="" disabled>Select country</option>
              <option v-for="country in countries" :key="country.value" :value="country.value">
                {{ country.text }}
              </option>
            </select>
            <p v-if="errors.countryCode" id="invoice-country-error" class="field-error">{{ errors.countryCode }}</p>
          </div>
          <div class="address-field">
            <label for="invoice-street">Street address</label>
            <input
              id="invoice-street"
              v-model="customer.addressLine"
              class="form-control"
              autocomplete="billing address-line1"
              :aria-invalid="!!errors.addressLine"
              aria-describedby="invoice-street-error"
            />
            <p v-if="errors.addressLine" id="invoice-street-error" class="field-error">{{ errors.addressLine }}</p>
          </div>
          <div class="address-field">
            <label for="invoice-extra">Address line 2 <span>(optional)</span></label>
            <input
              id="invoice-extra"
              v-model="customer.addressLine2"
              class="form-control"
              autocomplete="billing address-line2"
              placeholder="Apartment, suite or floor"
            />
          </div>
          <div class="address-locality">
            <div class="address-field">
              <label for="invoice-postcode">Postcode</label><input
                id="invoice-postcode"
                v-model="customer.postalCode"
                class="form-control"
                autocomplete="billing postal-code"
                :aria-invalid="!!errors.postalCode"
                aria-describedby="invoice-postcode-error"
              />
              <p v-if="errors.postalCode" id="invoice-postcode-error" class="field-error">{{ errors.postalCode }}</p>
            </div>
            <div class="address-field">
              <label for="invoice-city">City</label><input
                id="invoice-city"
                v-model="customer.city"
                class="form-control"
                autocomplete="billing address-level2"
                :aria-invalid="!!errors.city"
                aria-describedby="invoice-city-error"
              />
              <p v-if="errors.city" id="invoice-city-error" class="field-error">{{ errors.city }}</p>
            </div>
          </div>
        </fieldset>
        <p v-if="error" class="save-error" role="alert">{{ error }}</p>
        <div class="address-actions">
          <b-button class="btn-grey" :disabled="saving" @click="cancelEdit">Cancel</b-button><b-button type="submit" class="btn-green" :disabled="saving || !isFormChanged">
            {{ saving ? 'Saving…' : 'Save invoice address' }}
          </b-button>
        </div>
      </form>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Company, InvoiceAddressDraft } from '@/types/Models/Company'
import { BButton } from 'bootstrap-vue-next'
import { postcodeValidator } from 'postcode-validator'
import { computed, ref, watch, defineComponent, type ComputedRef, type Ref } from 'vue'
import { EDIT_COMPANY_ADDRESS_MODAL } from '@/const/ModalTypes'
import { useCustomersStore } from '@/store/customers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import countries from '@/assets/files/countries.json'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CompanyProxy from '@/proxies/CompanyProxy'

export default defineComponent({
  name: 'EditCompanyAddressModal',
  components: { BButton, ModalWrap },
  emits: ['update'],
  setup(_props, { emit }) {
    const modalStore = useVueNextModalStore()
    const customers = useCustomersStore()
    const modalName = EDIT_COMPANY_ADDRESS_MODAL
    const company: ComputedRef<Company> = computed<Company>(() => modalStore.modalPropsByName(modalName))
    const addressData: ComputedRef<Company['clientAddress']> = computed(() => company.value?.clientAddress)
    const empty = (): InvoiceAddressDraft => ({
      countryCode: '',
      addressLine: '',
      addressLine2: '',
      postalCode: '',
      city: '',
    })
    const customer: Ref<InvoiceAddressDraft> = ref<InvoiceAddressDraft>(empty())
    const original: Ref<InvoiceAddressDraft> = ref<InvoiceAddressDraft>(empty())
    const saving: Ref<boolean> = ref(false)
    const errors: Ref<Partial<Record<keyof InvoiceAddressDraft, string>>> = ref<
      Partial<Record<keyof InvoiceAddressDraft, string>>
    >({})
    const error: Ref<string> = ref('')
    const isFormChanged: ComputedRef<boolean> = computed(
      () => JSON.stringify(customer.value) !== JSON.stringify(original.value),
    )
    function reset(): void {
      customer.value = Object.fromEntries(
        Object.keys(empty()).map((key) => [key, addressData.value?.[key] || '']),
      ) as unknown as InvoiceAddressDraft
      original.value = { ...customer.value }
      errors.value = {}
      error.value = ''
    }
    watch(() => [company.value?.id, modalStore.isModalVisible(modalName)], reset, { immediate: true })
    function cancelEdit(): void {
      modalStore.closeModal(modalName)
    }
    function onSubmit(): void {
      if (saving.value || !isFormChanged.value) return
      const data = Object.fromEntries(
        Object.entries(customer.value).map(([key, value]) => [key, value.trim()]),
      ) as unknown as InvoiceAddressDraft
      errors.value = {}
      if (!countries.some((country) => country.value === data.countryCode))
        errors.value.countryCode = 'Select a country.'
      if (!data.addressLine) errors.value.addressLine = 'Enter a street address.'
      if (!data.city) errors.value.city = 'Enter a city.'
      if (!data.postalCode) errors.value.postalCode = 'Enter a postcode.'
      else if (data.countryCode) {
        try {
          if (!postcodeValidator(data.postalCode, data.countryCode))
            errors.value.postalCode = 'Enter a valid postcode for this country.'
        } catch {
          errors.value.postalCode = 'Postcode validation is unavailable for this country.'
        }
      }
      if (Object.keys(errors.value).length) return
      saving.value = true
      error.value = ''
      new CompanyProxy()
        .update(company.value.id, { clientAddress: data })
        .then(() => new CompanyProxy({ include: 'clientAddress,senderAddress' }).find(company.value.id))
        .then((response) => {
          const stored = response.data?.clientAddress
          if (!stored?.id) throw new Error('Invoice address was not returned')
          const item = customers.listCustomers?.find((item) => item.id === company.value.id)
          if (item) item.clientAddress = stored
          emit('update', { clientAddress: stored })
          cancelEdit()
        })
        .catch(() => {
          error.value = 'Could not confirm the saved address. Your entries are still here. Try again.'
        })
        .finally(() => {
          saving.value = false
        })
    }
    return {
      modalStore,
      customers,
      modalName,
      company,
      addressData,
      empty,
      customer,
      original,
      saving,
      errors,
      error,
      isFormChanged,
      reset,
      cancelEdit,
      onSubmit,
      countries,
    }
  },
})
</script>
